import { useState } from 'react';
import { usersApi, UserDetail } from '../api/users';

export interface UseProfileEditorParams {
  selectedUser: UserDetail | null;
  selectedUserId: string | null;
  onProfileSaved: (userId: string) => Promise<void>;
  onError: (msg: string) => void;
}

export function useProfileEditor({ selectedUser, selectedUserId, onProfileSaved, onError }: UseProfileEditorParams) {
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editProfileJson, setEditProfileJson] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  const startEditingProfile = () => {
    if (selectedUser) {
      const profileData = {
        user: selectedUser.user || {},
        device: selectedUser.device || {},
        custom: selectedUser.custom || {},
        settings: selectedUser.settings || {},
      };
      setEditProfileJson(JSON.stringify(profileData, null, 2));
      setIsEditingProfile(true);
    }
  };

  const cancelEditingProfile = () => {
    setIsEditingProfile(false);
    setEditProfileJson('');
  };

  const handleSaveProfile = async () => {
    if (!selectedUserId) return;

    try {
      setIsSavingProfile(true);

      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(editProfileJson);
      } catch {
        onError('JSON 형식이 올바르지 않습니다.');
        return;
      }

      await usersApi.update(selectedUserId, {
        user: parsed.user as Record<string, unknown> | undefined,
        device: parsed.device as Record<string, unknown> | undefined,
        custom: parsed.custom as Record<string, unknown> | undefined,
        settings: parsed.settings as Record<string, unknown> | undefined,
      });

      await onProfileSaved(selectedUserId);
      setIsEditingProfile(false);
    } catch (err) {
      console.error('Failed to save profile:', err);
      onError('프로필 저장에 실패했습니다.');
    } finally {
      setIsSavingProfile(false);
    }
  };

  return {
    isEditingProfile,
    editProfileJson,
    isSavingProfile,
    setEditProfileJson,
    setIsEditingProfile,
    startEditingProfile,
    cancelEditingProfile,
    handleSaveProfile,
  };
}
